import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import pickBy from 'lodash/pickBy';
import { GenericDataType, SmsDataType } from 'modules/domains/segmentation';
import { WORKFLOW_TRIGGERS } from 'modules/workflows/constants/base';
import type {
  ESFieldTypeDefinition,
  ESFieldTypeMap,
  ESTypeMap,
  SearchQueryField,
} from 'modules/workflows/types';
import { getESFieldDisplayName } from 'modules/workflows/utils/dataUtils/esMappingUtils';
import { EVENT_TYPES } from 'utils/dataTypes';
import {
  ADDED_ITEM_TO_CART_FIELDS,
  CAMPAIGN,
  CUSTOM_EVENT_FIELDS,
  LIST_MEMBERSHIP_SINGLE,
  MESSAGE_CHANNEL,
  MESSAGE_TYPE,
  USER_LIST_MEMBERSHIP_SINGLE,
} from 'utils/fieldConstants';
import { LONG } from 'utils/fieldTypes';
import { DEFAULT_SEARCH_DATATYPE } from './FilterInputsList.constants';
import { FilterSegmentSelectInputOption, PropertyMapConfig, Query } from './FilterInputsList.types';
import { createFilterSelectOption } from './utils';

/**
 * Determines whether a query should be added as a property option
 */
export const shouldAddFieldToPropertyMap = (
  query: Partial<Query>,
  propertyMap: Record<string, any>,
): boolean =>
  query.field != null && query.dataType === EVENT_TYPES.CUSTOM_EVENT && !propertyMap[query.field];

/**
 * Stubs a property item from a query
 */
export const createPropertyItemFromQuery = (query: Partial<Query> = {}) => ({
  field: query.field as string,
  fieldType: query.fieldType || '',
});

/**
 * Adds fields from the queries to the property option map
 */
const addCustomQueryFieldsToMap = (map: ESFieldTypeMap, queries: Partial<Query>[] = []) =>
  queries.reduce((acc: ESFieldTypeMap, query) => {
    if (query.field && shouldAddFieldToPropertyMap(query, acc)) {
      acc[query.field] = createPropertyItemFromQuery(query);
    }
    return acc;
  }, map);

const SUB_UNSUB_PROPERTY_MAP = {
  [MESSAGE_CHANNEL]: {
    fieldType: LONG,
    field: MESSAGE_CHANNEL,
  },
  [MESSAGE_TYPE]: {
    fieldType: LONG,
    field: MESSAGE_TYPE,
  },
};

const EMAIL_LIST = {
  [LIST_MEMBERSHIP_SINGLE]: {
    fieldType: LONG,
    field: LIST_MEMBERSHIP_SINGLE,
  },
};
const UUID_LIST = {
  [USER_LIST_MEMBERSHIP_SINGLE]: {
    fieldType: LONG,
    field: USER_LIST_MEMBERSHIP_SINGLE,
  },
};
/**
 * Gets a map of all the properties that can be used in a filter
 * This includes:
 * - all the properties for that dataType
 * - the campaign label field, if required for the datatype
 * - any custom fields added to the query, if required for the datatype
 */
export const getPropertyMap = ({
  mappings,
  dataType = DEFAULT_SEARCH_DATATYPE,
  initialQueries = [],
  currentQueries = [],
  isUUIDProject,
}: {
  mappings: ESTypeMap;
  dataType: string;
  initialQueries?: Query[];
  currentQueries?: Partial<Query>[];
  isUUIDProject: boolean;
}) => {
  const propertyMap = { ...(mappings?.[dataType] || {}) };

  switch (dataType) {
    case EVENT_TYPES.EMAIL_SUBSCRIBE:
    case EVENT_TYPES.EMAIL_UNSUBSCRIBE:
      // here we can supply static info. emailListIds, userListId, messageTypeId, channelId
      return { ...SUB_UNSUB_PROPERTY_MAP, ...(isUUIDProject ? UUID_LIST : EMAIL_LIST) };
    case EVENT_TYPES.CUSTOM_EVENT:
      return addCustomQueryFieldsToMap(propertyMap, [...initialQueries, ...currentQueries]);
    default:
      return propertyMap;
  }
};

export const createPropertySelectOption = (
  property: ESFieldTypeDefinition,
  dataType = DEFAULT_SEARCH_DATATYPE,
): FilterSegmentSelectInputOption => {
  const displayName = getESFieldDisplayName(property.field, dataType);
  return createFilterSelectOption(property.field, displayName, {
    dataType,
    field: property.field,
    fieldType: property.fieldType as string,
  });
};

/**
 * Returns an array of properties to be used in a select
 */
export const getPropertyOptions = (
  dataType: string = DEFAULT_SEARCH_DATATYPE,
  propertyMap: ESFieldTypeMap,
  propertyMapDisallowedFields: string[] = [],
): FilterSegmentSelectInputOption[] => {
  if (!dataType || !propertyMap) return [];
  // WF-5455 TODO: remove propertyMapDisallowedFields as this is already being handled in configurePropertyMap
  const propertyMapWithAllowedValues = filter(
    propertyMap,
    (item) => !propertyMapDisallowedFields.includes(item.field),
  );
  return Object.values(propertyMapWithAllowedValues)
    .map((property) => createPropertySelectOption(property, dataType))
    .sort((a, b) => (a.labelInMenu as string).localeCompare(b.labelInMenu as string));
};

/**
 * Clears timeouts that are currently running
 */
export const flushTimer = (timer?: number | null) => timer && clearTimeout(timer);

export const removeIdsFromMap = (map: ESFieldTypeMap, ids: string[]) => {
  const newMap = cloneDeep(map);
  ids.forEach((id) => {
    delete newMap[id];
  });
  return newMap;
};

// set.has() lookups are more performant than array.includes()
const CUSTOM_EVENT_SYSTEM_FIELDS_SET = new Set(CUSTOM_EVENT_FIELDS);

export const matchCustomEvents = (
  mapWithoutDisallowed: ESFieldTypeMap,
  propertyMapObjectFilterString: string,
) => {
  return pickBy(mapWithoutDisallowed, (_, key) => {
    if (key.indexOf('.') > -1) {
      // ex: "added to wishlist.email" or "added to wishlist.test.email"
      // !! We don't want "added to wishlist" itself
      return key !== propertyMapObjectFilterString && key.includes(propertyMapObjectFilterString);
    }
    if (CUSTOM_EVENT_SYSTEM_FIELDS_SET.has(key)) {
      // ex: "added to wishlist" is not in the key at all, so something like "createdAt" or other system fields
      return key;
    }
  });
};

/**
 * Returns a default configuration of allowed and disallowed fields for
 * customEvent, user, addedItemToCart
 */
export const getDefaultPropertyMapConfig = (config: {
  dataType: string;
  filterString?: string;
  initialSearchQueries?: SearchQueryField[];
  showInitialSelected?: boolean;
}) => {
  const { dataType, filterString, initialSearchQueries, showInitialSelected } = config;
  const initialFields = ((showInitialSelected && initialSearchQueries) || []).reduce(
    (acc: string[], query) => (acc.includes(query.field) ? acc : [...acc, query.field]),
    [],
  );

  const fields: Record<string, PropertyMapConfig> = {
    default: {
      // don't remove if user has it in their filters
      // or is Purchase event
      disallowed:
        initialFields.includes(CAMPAIGN) || dataType === GenericDataType.Purchase ? [] : [CAMPAIGN],
    },
    [WORKFLOW_TRIGGERS.ADDED_ITEM_TO_CART]: {
      allowed: ADDED_ITEM_TO_CART_FIELDS,
      disallowed: ADDED_ITEM_TO_CART_FIELDS,
    },
    [GenericDataType.CustomEvent]: {
      allowed: filterString ? [filterString, ...CUSTOM_EVENT_FIELDS] : CUSTOM_EVENT_FIELDS,
      ...(filterString && { disallowed: [filterString] }),
    },
    [GenericDataType.User]: {
      ...(filterString && { allowed: [filterString] }),
    },
    [SmsDataType.Click]: {
      allowed: [CAMPAIGN],
    },
  };

  const field = fields[dataType] || fields.default;
  const { allowed = [] } = field;
  return { ...field, allowed: allowed.length ? compact([...allowed, ...initialFields]) : [] };
};

/**
 * Returns a map with allowed properties
 *
 * A couple examples:
 *  - propertyMap fields: { cats, cats.meow, dogs, fat.cats, cats.meow.purr }
 *
 * If allowed = ['cats']
 *  - return { cats, cats.meow, cats.meow.purr }
 *
 * If allowed = ['cats.meow', 'fat.cats']
 *  - return { cats.meow, fat.cats, cats.meow.purr }
 *
 * If disallowed = ['cats']
 *  - return { cats.meow, dogs, fat.cats, cats.meow.purr }
 */
export const configurePropertyMap = (
  propertyMap: ESFieldTypeMap,
  config?: PropertyMapConfig,
): ESFieldTypeMap => {
  const { allowed = [], disallowed = [] } = config || {};
  // String to search for (useful if event has multiple dots)
  const filterString = allowed[0];
  if (!allowed.length && !disallowed.length) return propertyMap;

  const propertyMapWithAllowedFields = pickBy(propertyMap, (_, field) => {
    let isFieldAllowed = true;
    const fields = field?.split('.');

    if (allowed.length)
      isFieldAllowed =
        allowed.includes(field) || allowed.includes(fields[0]) || field.includes(filterString);
    if (disallowed.includes(field)) isFieldAllowed = false;
    if (isFieldAllowed) return field;
  });
  return propertyMapWithAllowedFields;
};
